import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { Observable, firstValueFrom, map } from 'rxjs';
import { Login } from '../models/login';
import { User } from '../models/user.model';
import { LoginRepository } from '../repositories/login.repository';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly TOKEN_KEY = 'token';
  private readonly EXPIRE_KEY = 'expires_at';
  private readonly USER_KEY = 'userDetails';

  constructor(
    private router: Router,
    private loginRepositoryHttp: LoginRepository,
  ) {}

  /**
   * Login
   */
  login(username: string, password: string): Observable<Login> {
    return this.loginRepositoryHttp.login(username, password).pipe(
      map((response) => {
        this.storeToken(response.headers?.get('set-cookie'));
        return response.body;
      }),
    );
  }

  /**
   * Valid Token Check
   */
  isTokenValid(): boolean {
    const token = this.getToken();
    const expiration = localStorage.getItem(this.EXPIRE_KEY);

    if (!token || !expiration) {
      return false;
    }

    return moment().isBefore(moment(JSON.parse(expiration)));
  }

  /**
   * Get Token
   */
  getToken(): string | undefined {
    return localStorage.getItem(this.TOKEN_KEY) || undefined;
  }

  /**
   * Save User
   */
  setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Get User
   */
  getUser(): User | null {
    const user = localStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  /**
   * Logout
   */
  async logout(token: string): Promise<boolean> {
    try {
      await firstValueFrom(this.loginRepositoryHttp.logout(token).pipe(map((res) => res.body)));
    } catch (error) {
      console.error(error);
    }

    this.clearSession();
    return this.router.navigateByUrl('/login');
  }

  /**
   * Save Token
   */
  private storeToken(cookie: string | null): void {
    if (cookie) {
      localStorage.setItem(this.TOKEN_KEY, cookie);
    }
  }

  /**
   * Clear Session
   */
  private clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.EXPIRE_KEY);
    localStorage.removeItem(this.USER_KEY);
  }
}
